// =============================================================================
// Web Push subscription helpers.
//
// Flow on the client:
//   1. User flips the "Enable notifications" toggle in Settings.
//   2. We ask the browser for permission.
//   3. We ask the registered service worker to subscribe with our VAPID
//      public key. The browser returns a `PushSubscription` object that
//      contains the push endpoint URL plus the p256dh / auth crypto keys
//      our server needs to encrypt messages.
//   4. We store that subscription in the `push_subscriptions` Supabase
//      table (one row per browser/device).
//   5. The Supabase Edge Function `send-push` later reads those rows and
//      POSTs encrypted messages to each endpoint.
// =============================================================================

import { supabase } from './supabase';
import { translate } from './i18n';
import { Capacitor } from '@capacitor/core';
// Imported statically on purpose: as a lazy chunk this module never finished
// loading inside the iOS WebView (the promise hung), which left the Settings
// switch dead. It is a registerPlugin() call and nothing else, so it costs
// nothing in the web bundle.
import { PushNotifications } from '@capacitor/push-notifications';

// VAPID public key — safe to ship in client code.
// Pair: private key lives only in the Supabase Edge Function secrets.
export const VAPID_PUBLIC_KEY =
  'BPABaPxspmRNhzMMFRkwuO4hPjkrz91LIbdjMgXCBMVYqyFW1uRhrwVAsAsaX5OrHMO061Rj4dnJH46ZGh7O_9A';

// Browsers want the application server key as a Uint8Array, not base64url.
const urlBase64ToUint8Array = (base64String) => {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = window.atob(base64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
};

// ---------------------------------------------------------------------------
// Native iOS (APNs). Web Push does not work inside the Capacitor shell, so on
// the native app every public function below routes to the Capacitor push
// plugin instead. The device token lands in the same push_subscriptions table
// as kind = 'apns' (supabase/apns.sql); send-push delivers through Apple.
// ---------------------------------------------------------------------------
const isNative = Capacitor.isNativePlatform();
const APNS_TOKEN_KEY = 'eulea_apns_token';
let nativePermission = 'prompt'; // refreshed by nativeCheckPermission()

// Plain function on purpose. The plugin object is a Proxy that answers every
// property, `then` included, so a promise that resolves with it (an `await`,
// an `import().then(m => m.Plugin)`) treats it as a thenable and never
// settles — which is what silently killed the Settings switch.
const nativePlugin = () => PushNotifications;

const nativeCheckPermission = async () => {
  try {
    const { receive } = await nativePlugin().checkPermissions();
    nativePermission = receive; // 'prompt' | 'granted' | 'denied'
  } catch (err) {
    // A plugin that cannot be reached is not the same as "the user said
    // no": keep 'prompt' so the switch stays usable and reports the error.
    // eslint-disable-next-line no-console
    console.error('[push] checkPermissions failed', err && err.message ? err.message : err);
    nativePermission = 'prompt';
  }
  return nativePermission;
};

const persistApnsToken = async (token) => {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('Not signed in');
  const { error } = await supabase.from('push_subscriptions').upsert(
    {
      user_id: user.id,
      endpoint: `apns:${token}`,
      kind: 'apns',
      device_token: token,
      p256dh: null,
      auth: null,
      user_agent: 'ios',
      last_seen: new Date().toISOString()
    },
    { onConflict: 'endpoint' }
  );
  if (error) throw error;
  try { window.localStorage.setItem(APNS_TOKEN_KEY, token); } catch (_) {}
};

// Register with APNs and resolve with the device token (or reject).
const nativeRegister = () =>
  new Promise(async (resolve, reject) => {
    const Push = nativePlugin();
    const timer = setTimeout(() => reject(new Error('APNs registration timed out')), 15000);
    const ok = await Push.addListener('registration', ({ value }) => {
      clearTimeout(timer);
      ok.remove(); bad.remove();
      resolve(value);
    });
    const bad = await Push.addListener('registrationError', (err) => {
      clearTimeout(timer);
      ok.remove(); bad.remove();
      reject(new Error(err?.error || 'APNs registration failed'));
    });
    await Push.register();
  });

const nativeEnable = async () => {
  const Push = nativePlugin();
  let perm = (await Push.checkPermissions()).receive;
  if (perm === 'prompt' || perm === 'prompt-with-rationale') perm = (await Push.requestPermissions()).receive;
  nativePermission = perm;
  if (perm !== 'granted') throw new Error('Permission denied');
  const token = await nativeRegister();
  await persistApnsToken(token);
  return token;
};

const nativeDisable = async () => {
  let token = null;
  try { token = window.localStorage.getItem(APNS_TOKEN_KEY); } catch (_) {}
  if (token) {
    await supabase.from('push_subscriptions').delete().eq('endpoint', `apns:${token}`);
    try { window.localStorage.removeItem(APNS_TOKEN_KEY); } catch (_) {}
  }
};

// On launch: if this device was registered before, refresh the token (Apple
// may rotate it) and wire the tap handler that opens the notification's url.
export const initNativePush = async () => {
  if (!isNative) return;
  try {
    await nativeCheckPermission();
    const Push = nativePlugin();
    await Push.addListener('pushNotificationActionPerformed', (action) => {
      const url = action?.notification?.data?.url;
      if (typeof url === 'string' && url.startsWith('eulea://')) window.location.href = url;
    });
    let token = null;
    try { token = window.localStorage.getItem(APNS_TOKEN_KEY); } catch (_) {}
    if (token && nativePermission === 'granted') {
      const fresh = await nativeRegister().catch(() => null);
      if (fresh) await persistApnsToken(fresh).catch(() => {});
    }
  } catch {
    // best effort
  }
};

export const isPushSupported = () =>
  isNative ||
  (typeof window !== 'undefined' &&
    'serviceWorker' in navigator &&
    'PushManager' in window &&
    'Notification' in window);

export const getNotificationPermission = () => {
  if (isNative) return nativePermission === 'granted' ? 'granted' : nativePermission === 'denied' ? 'denied' : 'default';
  return isPushSupported() ? Notification.permission : 'denied';
};

// Pull the existing subscription (if any) from the active SW registration.
const getActiveRegistration = async () => {
  if (!isPushSupported()) return null;
  const reg = await navigator.serviceWorker.getRegistration();
  return reg || null;
};

// PushSubscription → { endpoint, p256dh, auth } strings for the DB.
const serializeSubscription = (sub) => {
  const json = sub.toJSON();
  return {
    endpoint: json.endpoint,
    p256dh: json.keys?.p256dh,
    auth: json.keys?.auth
  };
};

// Insert/upsert into push_subscriptions for the current user.
const persistSubscription = async (sub) => {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('Not signed in');
  const { endpoint, p256dh, auth } = serializeSubscription(sub);
  const { error } = await supabase.from('push_subscriptions').upsert(
    {
      user_id: user.id,
      endpoint,
      p256dh,
      auth,
      user_agent: navigator.userAgent,
      last_seen: new Date().toISOString()
    },
    { onConflict: 'endpoint' }
  );
  if (error) throw error;
};

// Remove the row matching this subscription's endpoint.
const removeSubscriptionRow = async (endpoint) => {
  const { error } = await supabase
    .from('push_subscriptions')
    .delete()
    .eq('endpoint', endpoint);
  if (error) throw error;
};

// Public: ask the user, subscribe, and persist.
export const enablePushNotifications = async () => {
  if (isNative) return nativeEnable();
  if (!isPushSupported()) throw new Error('Push not supported in this browser');
  const reg = await getActiveRegistration();
  if (!reg) throw new Error('Service worker not registered yet');

  // Permission prompt. Must be triggered from a user gesture.
  let perm = Notification.permission;
  if (perm === 'default') perm = await Notification.requestPermission();
  if (perm !== 'granted') throw new Error('Permission denied');

  // Reuse or create a subscription.
  let sub = await reg.pushManager.getSubscription();
  if (!sub) {
    sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY)
    });
  }

  await persistSubscription(sub);
  return sub;
};

// Public: unsubscribe + delete the row.
export const disablePushNotifications = async () => {
  if (isNative) return nativeDisable();
  const reg = await getActiveRegistration();
  if (!reg) return;
  const sub = await reg.pushManager.getSubscription();
  if (!sub) return;
  const endpoint = sub.endpoint;
  await sub.unsubscribe().catch(() => {});
  await removeSubscriptionRow(endpoint).catch(() => {});
};

// Public: fire a test push to the current user via the Edge Function.
// Returns the parsed response body (e.g. { sent: 1 }) or throws on failure.
export const sendTestPush = async () => {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session) throw new Error('Not signed in');
  const url = supabase.supabaseUrl.replace(/\/$/, '') + '/functions/v1/send-push';
  const res = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${session.access_token}`
    },
    body: JSON.stringify({
      title: translate('settings.pushTestTitle'),
      body: translate('settings.pushTestBody'),
      url: '/',
      tag: 'eulea-test'
    })
  });
  const text = await res.text();
  let json;
  try { json = JSON.parse(text); } catch (_) { json = { raw: text }; }
  if (!res.ok) throw new Error(json.error || `HTTP ${res.status}`);
  return json;
};

// Public: is this device currently subscribed?
export const isCurrentlySubscribed = async () => {
  if (isNative) {
    await nativeCheckPermission();
    let token = null;
    try { token = window.localStorage.getItem(APNS_TOKEN_KEY); } catch (_) {}
    return nativePermission === 'granted' && !!token;
  }
  if (!isPushSupported()) return false;
  if (Notification.permission !== 'granted') return false;
  const reg = await getActiveRegistration();
  if (!reg) return false;
  const sub = await reg.pushManager.getSubscription();
  return Boolean(sub);
};
